import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Home, TrendingUp, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import DataQualityBadge from './DataQualityBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const gbp = (n) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(n);

const gbpK = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}m`;
  if (Math.abs(n) >= 1_000) return `£${Math.round(n / 1000)}k`;
  return `£${Math.round(n)}`;
};

/**
 * UkSoldPricesCard — public, read-only sold-price comparables for a UK postcode.
 *
 * Data source: HM Land Registry "Price Paid Data" (Open Government Licence v3.0,
 * Crown copyright). Free, no auth, no scraping. Ingested daily via
 * `/api/admin/uk-land-registry/ingest` and surfaced through `/api/uk/sold-prices`.
 *
 * Renders:
 *   - Headline: median sold price + count over the lookback window
 *   - Min / max range
 *   - Up to 5 recent comparables (address, date, price, type, tenure)
 *
 * Used on:
 *   - PropertyDetailPage (UK listings) — comparables within the property's outward postcode
 *   - UkLandingPage — "What's selling near you" hook
 *   - PriceIndexPage (UK fallback, optional)
 *
 * Props:
 *   postcode    string  — UK postcode, full or outward. Required.
 *   months      number  — lookback window in months. Default 24.
 *   limit       number  — max comparables to show. Default 5.
 *   compact     boolean — hide the comparables table, show just the headline summary.
 */
const UkSoldPricesCard = ({ postcode, months = 24, limit = 5, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!postcode || !postcode.trim()) {
      setLoading(false);
      setErr('no_postcode');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios
      .get(`${BACKEND_URL}/api/uk/sold-prices`, {
        params: { postcode: postcode.trim(), months, limit },
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postcode, months, limit]);

  if (loading) {
    return (
      <Card data-testid="uk-sold-prices-card-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-[#7042cc]" />
          Loading recent sold prices…
        </CardContent>
      </Card>
    );
  }

  if (err === 'no_postcode' || !postcode) return null;

  if (err) {
    return (
      <Card data-testid="uk-sold-prices-card-error">
        <CardContent className="p-5 text-sm text-gray-500">
          Couldn't load sold-price comparables for this area.
        </CardContent>
      </Card>
    );
  }

  if (!data || data.count === 0) {
    return (
      <Card data-testid="uk-sold-prices-card-empty">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Home className="h-4 w-4 text-[#7042cc]" />
            <h3 className="text-sm font-semibold text-gray-900">
              Recent sold prices
            </h3>
            <Badge variant="outline" className="text-[10px] font-mono">
              {data?.postcode_outward || postcode}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            No HM Land Registry sales recorded in this postcode over the last{' '}
            {months} months. England & Wales only — Scotland uses the
            Registers of Scotland and Northern Ireland uses the Land &amp;
            Property Services register.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="uk-sold-prices-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-[#7042cc]" />
              <h3 className="text-sm font-semibold text-gray-900">
                Recent sold prices
              </h3>
              <Badge variant="outline" className="text-[10px] font-mono">
                {data.postcode_outward}
              </Badge>
            </div>
            <p className="text-[11px] text-gray-500">
              {data.count} {data.count === 1 ? 'sale' : 'sales'} in the last{' '}
              {months} months · HM Land Registry
            </p>
            <div className="mt-1.5">
              <DataQualityBadge market="UK" />
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
              Median
            </p>
            <p
              className="text-xl sm:text-2xl font-extrabold text-gray-900 tabular-nums"
              data-testid="uk-sold-prices-median"
            >
              {gbp(data.median_price)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
          <div className="rounded bg-gray-50 p-2 border">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">
              Low
            </p>
            <p className="font-semibold tabular-nums">{gbpK(data.min_price)}</p>
          </div>
          <div className="rounded bg-gray-50 p-2 border">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">
              High
            </p>
            <p className="font-semibold tabular-nums">{gbpK(data.max_price)}</p>
          </div>
        </div>

        {!compact && data.results && data.results.length > 0 && (
          <div className="space-y-2" data-testid="uk-sold-prices-list">
            {data.results.slice(0, limit).map((r, i) => (
              <div
                key={`${r.postcode}-${r.deed_date}-${i}`}
                className="flex items-start justify-between gap-2 text-xs border-t border-gray-100 pt-2"
                data-testid={`uk-sold-row-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {r.address || r.street || r.postcode}
                  </p>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="h-2.5 w-2.5" />
                    {r.postcode} · {r.property_type_label} ·{' '}
                    {r.tenure_label} {r.is_new && '· New build'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-gray-900 tabular-nums">
                    {gbp(r.price)}
                  </p>
                  <p className="text-[10px] text-gray-500">{r.deed_date}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
          Source: HM Land Registry Price Paid Data, Crown copyright, Open
          Government Licence v3.0. Updated monthly.
        </p>
      </CardContent>
    </Card>
  );
};

export default UkSoldPricesCard;
